import { useState, useEffect, useRef } from 'react';

interface StatusPollingOptions {
    pollingInterval?: number;
    errorInterval?: number;
}

interface StatusState<T> {
    data: T | undefined;
    isLoading: boolean;
    error?: string;
}

export function useStatusPolling<T>(
    fetchFn: () => Promise<T>,
    errorIdentifier: string,
    options: StatusPollingOptions = {}
): [StatusState<T>, () => void] {
    const [state, setState] = useState<StatusState<T>>({
        data: undefined,
        isLoading: true,
    });

    const mountedRef = useRef(true);
    const initialLoadRef = useRef(true);

    const checkStatus = async () => {
        if (!mountedRef.current) return;

        if (initialLoadRef.current) {
            setState(prev => ({ ...prev, isLoading: true, error: undefined }));
        }

        try {
            const data = await fetchFn();
            if (!mountedRef.current) return;
            initialLoadRef.current = false;
            setState({
                data,
                isLoading: false,
                error: undefined,
            });
            return false;
        } catch (error) {
            if (!mountedRef.current) return;
            initialLoadRef.current = false;
            const errorMsg = `Failed to connect to ${errorIdentifier}`;
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMsg,
            }));
            return true;
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        let timeoutId: ReturnType<typeof setTimeout>;

        const poll = async () => {
            const hasError = await checkStatus();
            if (!mountedRef.current) return;

            const interval = hasError
                ? (options.errorInterval || 30000)
                : (options.pollingInterval || 5000);

            timeoutId = setTimeout(poll, interval);
        };

        poll();

        return () => {
            mountedRef.current = false;
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line
    }, [options.pollingInterval, options.errorInterval]);

    return [state, checkStatus];
}
